import { Timestamp } from "@bufbuild/protobuf";

import React, { useContext, useEffect, useState } from "react";

import { Autorenew } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import { DateTime } from "luxon";
import { Clipboard } from "react-feather";
import mainStyles from "styles";

import { Asset } from "@safetyculture/s12-apis-connect-web/s12/assets/admin/v1/asset_pb";
import { AssetState } from "@safetyculture/s12-apis-connect-web/s12/assets/v1/asset_pb";

import { listAssets as listAssetsApi } from "api/endpoints/assets";
import { callOptionsHeaders } from "api/helpers";
import EmptyTableResults from "components/shared/table/empty-table-results";
import { SnackbarContext } from "context/snackbar-context";
import downloadFile from "helpers/download-file";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";

interface OrgAssetsTabProps {
  roleId: string;
  regionCode: string;
}

const assetStateDisplayMapping = {
  [AssetState.ACTIVE]: "Active",
  [AssetState.ARCHIVED]: "Archived",
  [AssetState.UNSPECIFIED]: "Unspecified",
};

const DEFAULT_PAGE_SIZE = 50;

const OrgAssetsTab = ({ roleId, regionCode }: OrgAssetsTabProps) => {
  const theme = useTheme();
  const classes = mainStyles();

  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetQueryFilter, setAssetQueryFilter] = useState<string>("");
  const [pageToken, setPageToken] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExportingCsv, setIsExportingCsv] = useState<boolean>(false);

  const { showError, copyToClipboard } = useContext(SnackbarContext);

  const formatDisplayNameField = (asset: Asset) => {
    const displayNameField = asset.fields.find(
      (field) => field.name === "Display name",
    );
    if (!displayNameField?.value.value) {
      return "";
    }

    return displayNameField.value.value.toString();
  };

  const formatCreatedAtString = (timestamp: Timestamp | undefined) => {
    if (timestamp) {
      const date = convertTimestampToDate(timestamp);
      const formattedDate = DateTime.fromJSDate(date).toFormat("dd MMM yyyy");
      return formattedDate;
    }
    return "-";
  };

  const formatLastInspectedAtString = (timestamp: Timestamp | undefined) => {
    if (timestamp) {
      const date = convertTimestampToDate(timestamp);
      const formattedDate = DateTime.fromJSDate(date).toFormat("dd MMM yyyy");
      const timeSinceDate = DateTime.fromJSDate(date).toRelative();
      return `${formattedDate} (${timeSinceDate})`;
    }
    return "-";
  };

  const listAssets = (pageToken?: string) => {
    setIsLoading(true);

    listAssetsApi(
      {
        orgId: roleId,
        pageToken: pageToken,
        pageSize: DEFAULT_PAGE_SIZE,
        search: assetQueryFilter,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((resp) => {
        setAssets((prev) => [...prev, ...resp.assets]);
        setPageToken(resp.nextPageToken);
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  };

  const handleListAssets = () => {
    setAssets([]);
    listAssets();
  };

  const handleExportCsv = async () => {
    setIsExportingCsv(true);
    const allAssets: Asset[] = [];
    let token = "";

    do {
      const listAssetsResp = await listAssetsApi(
        {
          orgId: roleId,
          pageToken: token,
          pageSize: DEFAULT_PAGE_SIZE,
          search: assetQueryFilter,
        },
        callOptionsHeaders(regionCode, roleId),
      );

      allAssets.push(...listAssetsResp.assets);
      token = listAssetsResp.nextPageToken;
    } while (token != "");

    const csvData = allAssets.map((row) => {
      return {
        uniqueID: row.code,
        id: row.id,
        type: row.type?.name,
        displayName: formatDisplayNameField(row),
        site: row.site?.name,
        createdAt:
          row.createdAt &&
          formatDateTimeDisplay(convertTimestampToDate(row.createdAt)),
        inspectedAt:
          row.inspectedAt &&
          formatDateTimeDisplay(convertTimestampToDate(row.inspectedAt)),
        state: assetStateDisplayMapping[row.state],
      };
    });

    const csvHeaders = [
      "Unique ID",
      "ID",
      "Type",
      "Display name",
      "Site",
      "Created at",
      "Last inspected at",
      "State",
    ];

    const csv = [
      csvHeaders.join(","),
      ...csvData.map((row) => {
        return Object.values(row)
          .map((cell) => {
            return cell ? `"${cell.replace(/"/g, '""')}"` : '""';
          })
          .join(",");
      }),
    ].join("\n");

    const fileName = `${roleId} - assets.csv`;
    downloadFile(fileName, csv);

    setIsExportingCsv(false);
  };

  const displayAssetRow = (asset: Asset) => {
    return (
      <TableRow>
        <TableCell>{asset.code}</TableCell>
        <TableCell>
          <Typography
            variant="body2"
            align="left"
            sx={classes.clickable}
            onClick={() => copyToClipboard(asset.id)}
          >
            {asset.id}{" "}
            <Clipboard
              color={theme.palette.text.primary}
              size={"1rem"}
              className="position-icon"
            />
          </Typography>
        </TableCell>
        <TableCell>{asset.type ? asset.type.name : "-"}</TableCell>
        <TableCell>{formatDisplayNameField(asset) || "-"}</TableCell>
        <TableCell>{asset.site ? asset.site.name : "-"}</TableCell>
        <TableCell>{formatCreatedAtString(asset.createdAt)}</TableCell>
        <TableCell>{formatLastInspectedAtString(asset.inspectedAt)}</TableCell>
        <TableCell>{assetStateDisplayMapping[asset.state]}</TableCell>
      </TableRow>
    );
  };

  useEffect(() => {
    listAssets();
    // Load assets on initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container sx={classes.spaceRight} justifyContent={"flex-end"}>
      <Grid container justifyContent={"flex-end"} margin={"1em"}>
        <Grid container direction="row" spacing={3}>
          <Grid item xs>
            <TextField
              id="asset-search"
              label="Asset Unique ID"
              value={assetQueryFilter}
              onChange={(e) => setAssetQueryFilter(e.target.value)}
              variant="outlined"
              size="small"
              style={{ width: "100%" }}
              inputProps={{ "data-testid": "asset-search" }}
              onKeyUp={(event) => {
                if (event.key === "Enter") {
                  return;
                }
              }}
              disabled={isLoading || isExportingCsv}
            />
          </Grid>
          <Grid item>
            <Button
              onClick={handleListAssets}
              variant="contained"
              color="primary"
              disabled={isExportingCsv}
              data-testid="asset-search-btn"
            >
              Search
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={handleExportCsv}
              variant="contained"
              color="secondary"
              disabled={isExportingCsv}
              data-testid="export-users-csv-button"
              startIcon={
                isExportingCsv ? <Autorenew className="spin-1s" /> : null
              }
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <TableContainer component={Paper}>
        <Table aria-label="org-assets-table">
          <TableHead>
            <TableRow>
              <TableCell>
                <b>Unique ID</b>
              </TableCell>
              <TableCell>
                <b>Asset ID</b>
              </TableCell>
              <TableCell>
                <b>Type</b>
              </TableCell>
              <TableCell>
                <b>Display name</b>
              </TableCell>
              <TableCell>
                <b>Site</b>
              </TableCell>
              <TableCell>
                <b>Created at</b>
              </TableCell>
              <TableCell>
                <b>Last inspected at</b>
              </TableCell>
              <TableCell>
                <b>State</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && assets.length === 0 && (
              <EmptyTableResults colSpan={8} tableHeading={"assets"} />
            )}
            {assets.length > 0 && assets.map((asset) => displayAssetRow(asset))}
          </TableBody>
          <TableFooter>
            <TableCell
              colSpan={8}
              className={clsx(
                classes.paddingTopSmall,
                classes.paddingBottomSmall,
              )}
            >
              <Box display="flex" justifyContent="flex-end">
                <Box>
                  {pageToken === "" ? (
                    "You're all caught up"
                  ) : (
                    <Button
                      color="primary"
                      size="small"
                      onClick={() => listAssets(pageToken)}
                      data-testid="load-more-assets-btn"
                    >
                      ...SEE MORE
                    </Button>
                  )}
                </Box>
              </Box>
            </TableCell>
          </TableFooter>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export default OrgAssetsTab;
